import { useEffect, useMemo, useState } from 'react';

import ClusterApi from '../api/ClusterApi';
import EventsDataTable from '../types/instance/tables/EventsDataTable';

export const availabilityGroupKey = (instanceId: number, groupName: string) => `${instanceId}|${groupName}`;

/**
 * Resolves the availability group screen path for event rows raised against an
 * availability group, keyed by availabilityGroupKey. The event carries only the
 * datasource and group name, so the cluster and group ids come from the
 * topology. Rows with no AG behind them, or none resolved yet, have no entry.
 */
export function useAvailabilityGroupLinks(rows: EventsDataTable[]): Map<string, string> {
    const [links, setLinks] = useState<Map<string, string>>(new Map());

    // A stable key, so a new array of the same instances does not refetch.
    const instanceIds = useMemo(() => Array.from(new Set(rows
        .filter(row => row.object === 'availabilitygroup' && row.groupName)
        .map(row => row.instanceId))).sort((a, b) => a - b).join(','), [rows]);

    useEffect(() => {
        if (!instanceIds) {
            setLinks(new Map());
            return;
        }
        let active = true;
        // The topology is cached in ClusterApi, so this is one request however
        // many instances are asked about.
        Promise.all(instanceIds.split(',').map(Number).map(instanceId =>
            ClusterApi.getInstanceMembership(instanceId).then(groups =>
                groups.map(group => [
                    availabilityGroupKey(instanceId, group.groupname),
                    `/clusters/${group.clusterid}/availability-groups/${group.groupid}`,
                ] as [string, string]))))
            .then(entries => {
                if (active) setLinks(new Map(entries.flat()));
            })
            // Falls back to the instance link rather than failing the table.
            .catch(error => console.error('Failed to resolve availability group links.', error));
        return () => {
            active = false;
        };
    }, [instanceIds]);

    return links;
}
